// ** Modules
import { useState } from 'react';

// ** UI
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

// ** Types
import { alpha } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import type { DashboardConsultingSection } from './types';

// ** Shared
import { DashboardBodyText, DashboardCtaLink } from './shared';

type DashboardConsultingPanelProps = {
	consulting: DashboardConsultingSection;
};

const DashboardConsultingPanel = ({ consulting }: DashboardConsultingPanelProps) => {
	const buildItemKeys = (items: string[]) => {
		const counts = new Map<string, number>();

		return items.map((item) => {
			const occurrence = (counts.get(item) ?? 0) + 1;
			counts.set(item, occurrence);

			return { key: `${item}-${occurrence}`, item };
		});
	};

	const hasItems = consulting.items.length > 0;
	const firstItem = consulting.items[0];
	const remainingItems = consulting.items.slice(1);
	const keyedRemainingItems = buildItemKeys(remainingItems);
	const keyedItems = buildItemKeys(consulting.items);
	const isCollapsible = consulting.collapsible === true && hasItems;

	const [isExpanded, setIsExpanded] = useState(isCollapsible ? (consulting.defaultExpanded ?? false) : true);

	const toggle = () => setIsExpanded((v) => !v);

	const dividerSx = {
		width: '100%',
		borderColor: (theme: Theme) => alpha(theme.palette.common.black, 0.18),
		borderBottomWidth: '1px'
	};

	const chevronButtonSx: SxProps<Theme> = {
		p: 0.75,
		flexShrink: 0,
		borderRadius: 999,
		color: 'text.secondary',
		backgroundColor: 'transparent',
		transition: 'background-color 180ms ease, transform 180ms ease',
		'&:hover': {
			backgroundColor: (theme: Theme) => alpha(theme.palette.common.black, 0.06),
			transform: 'translateY(-1px)'
		},
		'&:active': { transform: 'translateY(0) scale(0.97)' },
		'&:focusVisible': {
			outline: (theme: Theme) => `2px solid ${alpha(theme.palette.common.black, 0.35)}`,
			outlineOffset: '2px'
		},
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
			'&:hover': { transform: 'none' },
			'&:active': { transform: 'none' }
		}
	};

	const chevronIcon = (
		<IconButton
			aria-label={isExpanded ? 'Beratungen ausblenden' : 'Beratungen einblenden'}
			aria-expanded={isExpanded}
			onClick={toggle}
			disableRipple
			sx={chevronButtonSx}
		>
			<ExpandMoreIcon
				sx={{
					transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
					transition: 'transform 260ms cubic-bezier(0.22, 1, 0.36, 1)'
				}}
			/>
		</IconButton>
	);

	const collapseProps = {
		timeout: { enter: 320, exit: 220 },
		easing: {
			enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
			exit: 'cubic-bezier(0.4, 0, 1, 1)'
		},
		collapsedSize: '0px' as const
	};

	return (
		<Box
			component="section"
			aria-label={consulting.title}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				minWidth: 0,
				backgroundColor: 'common.white',
				borderRadius: '8px',
				p: { xs: 4, md: 8 },
				// On mobile: tighter bottom padding when collapsed (no CTA visible)
				pb: { xs: isCollapsible && !isExpanded ? 4 : 8, md: 8 }
			}}
		>
			{/* Unified header: title + CTA (desktop-only) or chevron (mobile-only) */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 4,
					mb: { xs: 0, md: 8 }
				}}
			>
				<Typography variant="panel-heading" component="h2" sx={{ m: 0, flex: '1 1 auto', color: 'common.black' }}>
					{consulting.title}
				</Typography>
				{consulting.cta ? (
					<Box sx={{ display: { xs: 'none', md: 'block' } }}>
						<DashboardCtaLink cta={consulting.cta} />
					</Box>
				) : null}
				{isCollapsible ? <Box sx={{ display: { xs: 'flex', md: 'none' } }}>{chevronIcon}</Box> : null}
			</Box>

			{/* Desktop: first item always visible + collapse chevron */}
			{hasItems ? (
				<Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
					<DashboardBodyText sx={{ py: 4, flex: '1 1 auto' }}>{firstItem}</DashboardBodyText>
					{isCollapsible ? chevronIcon : null}
				</Box>
			) : (
				<DashboardBodyText sx={{ display: { xs: 'none', md: 'block' } }}>
					{consulting.emptyText ?? 'Keine Beratung hinterlegt'}
				</DashboardBodyText>
			)}

			{/* Desktop: remaining items, animated collapse */}
			{hasItems && remainingItems.length > 0 ? (
				<Collapse
					{...collapseProps}
					in={isCollapsible ? isExpanded : true}
					sx={{ display: { xs: 'none', md: 'block' } }}
				>
					{keyedRemainingItems.map(({ key, item }) => (
						<Box key={key}>
							<Divider sx={dividerSx} />
							<DashboardBodyText sx={{ py: 4 }}>{item}</DashboardBodyText>
						</Box>
					))}
				</Collapse>
			) : null}

			{/* Mobile: collapsible content — all items + CTA */}
			<Collapse {...collapseProps} in={isCollapsible ? isExpanded : true} sx={{ display: { xs: 'block', md: 'none' } }}>
				<Box sx={{ mt: 8 }}>
					{hasItems ? (
						<Box sx={{ display: 'flex', flexDirection: 'column' }}>
							{keyedItems.map(({ key, item }, index) => (
								<Box key={key}>
									<DashboardBodyText sx={{ py: 4 }}>{item}</DashboardBodyText>
									{index < keyedItems.length - 1 ? <Divider sx={dividerSx} /> : null}
								</Box>
							))}
						</Box>
					) : (
						<DashboardBodyText>{consulting.emptyText ?? 'Keine Beratung hinterlegt'}</DashboardBodyText>
					)}

					{consulting.cta ? (
						<Box sx={{ mt: 8 }}>
							<DashboardCtaLink cta={consulting.cta} sx={{ textAlign: 'left', width: 'auto' }} />
						</Box>
					) : null}
				</Box>
			</Collapse>
		</Box>
	);
};

export default DashboardConsultingPanel;
